import type { BacklineItem, ProvidedBy } from '../types';
import NumberPicker from './NumberPicker';
import AutoTextarea from './AutoTextarea';
import BacklineImageUploader from './BacklineImageUploader';
import Button from './ui/Button';
import { useBacklineTypes } from '../hooks/useSuggestionList';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * BacklineEditor — the musician/crew backline list editor, shared by
 * MusicianEditPage and CrewEditPage.
 *
 * Rendered as a TABLE with column headers (same visual language as the
 * instrument tracks table): ONE shared grid where every row is `display:
 * contents`, so the header and all data rows share identical column tracks.
 * On narrow screens the whole table scrolls horizontally inside its container
 * (like .tracks-section), never wrapping fields. (2026-08-14 — user: "même
 * rendu que le tableau des pistes".)
 *
 * Props-driven: the parent owns the item array + mutation callbacks.
 */
export interface BacklineEditorProps {
  items: BacklineItem[];
  onChange: (items: BacklineItem[]) => void;
  datalistId?: string;
}

export default function BacklineEditor({ items, onChange, datalistId = 'backline-types-suggestions' }: BacklineEditorProps) {
  const { t } = useTranslation();
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const { types: backlineTypes, put: putBacklineTypes } = useBacklineTypes();

  const list = Array.isArray(items) ? items : [];

  const update = useCallback((itemId: string, patch: Partial<BacklineItem>) => {
    onChange(list.map((it) => (it.id === itemId ? { ...it, ...patch } : it)));
  }, [list, onChange]);

  const remove = useCallback((itemId: string) => {
    onChange(list.filter((it) => it.id !== itemId));
  }, [list, onChange]);

  const move = useCallback((itemId: string, dir: -1 | 1) => {
    const i = list.findIndex((it) => it.id === itemId);
    if (i < 0) return;
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  }, [list, onChange]);

  const moveTo = useCallback((dragIdx: number, dropIdx: number) => {
    if (dragIdx === dropIdx) return;
    const next = [...list];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(dropIdx, 0, moved);
    onChange(next);
  }, [list, onChange]);

  // Auto-add a backline type typed by hand into the shared suggestion list.
  const ensureType = useCallback((t: string) => {
    const v = (t || '').trim();
    if (!v) return;
    if (!backlineTypes.some((x) => x.toLowerCase() === v.toLowerCase())) {
      void putBacklineTypes([...backlineTypes, v]);
    }
  }, [backlineTypes, putBacklineTypes]);

  return (
    <>
      <div className="bl-section">
        <div className="bl-table">
          {/* Column headers */}
          <div className="bl-row bl-header">
            <span className="bl-handle" title={t('backlineEditor.dragToReorder')} />
            <span className="bl-qty">{t('backlineEditor.qty')}</span>
            <span className="bl-type">{t('backlineEditor.type')}</span>
            <span className="bl-label">{t('backlineEditor.description')}</span>
            <span className="bl-notes">{t('backlineEditor.notes')}</span>
            <span className="bl-provider">{t('backlineEditor.providedBy')}</span>
            <span className="bl-img">{t('backlineEditor.image')}</span>
            <span className="bl-actions" />
          </div>

          {/* Rows */}
          {list.map((item, bi) => (
            <div
              key={item.id}
              className={`bl-row bl-data ${dragIdx === bi ? 'bl-dragging' : ''}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); if (dragIdx !== null) { moveTo(dragIdx, bi); setDragIdx(null); } }}
              onDragEnd={() => setDragIdx(null)}
            >
              <div className="bl-handle">
                <span
                  className="field-drag-handle"
                  draggable
                  onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDragIdx(bi); }}
                  title={t('backlineEditor.dragToReorder')}
                >⋮⋮</span>
                <Button variant="ghost" size="sm" disabled={bi === 0} onClick={() => move(item.id, -1)} aria-label={t('backlineEditor.moveUp')}>↑</Button>
                <Button variant="ghost" size="sm" disabled={bi === list.length - 1} onClick={() => move(item.id, 1)} aria-label={t('backlineEditor.moveDown')}>↓</Button>
              </div>
              <div className="bl-qty">
                <NumberPicker
                  value={item.quantity}
                  options={[1, 2, 3, 4, 5, 6, 8, 10]}
                  onChange={(v) => update(item.id, { quantity: v || 1 })}
                  className="backline-qty"
                />
              </div>
              <div className="bl-type">
                <input
                  type="text"
                  list={datalistId}
                  value={item.type}
                  onChange={(e) => update(item.id, { type: e.target.value })}
                  onBlur={(e) => ensureType(e.target.value)}
                  placeholder={t('backlineEditor.typePh')}
                  className="td-input"
                />
              </div>
              <div className="bl-label">
                <AutoTextarea
                  value={item.label}
                  onChange={(e) => update(item.id, { label: e.target.value })}
                  placeholder={t('backlineEditor.descPh')}
                  className="td-input"
                />
              </div>
              <div className="bl-notes">
                <AutoTextarea
                  value={item.notes}
                  onChange={(e) => update(item.id, { notes: e.target.value })}
                  placeholder={t('backlineEditor.notes')}
                  className="td-input"
                />
              </div>
              <div className="bl-provider">
                <select
                  className="td-input"
                  value={item.providedBy ?? 'band'}
                  onChange={(e) => update(item.id, { providedBy: e.target.value as ProvidedBy })}
                  title={t('backlineEditor.providedTitle')}
                >
                  <option value="band">{t('backlineEditor.band')}</option>
                  <option value="venue">{t('backlineEditor.venue')}</option>
                </select>
              </div>
              <div className="bl-img">
                <BacklineImageUploader
                  imageUrl={item.imageUrl}
                  onChange={(imageUrl) => update(item.id, { imageUrl })}
                />
              </div>
              <div className="bl-actions">
                <Button
                  variant="ghost"
                  size="sm"
                  className="btn-danger-icon"
                  onClick={() => remove(item.id)}
                  aria-label={t('backlineEditor.removeItem')}
                >
                  🗑
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* datalist must live OUTSIDE the grid (a grid child would become a column) */}
      <datalist id={datalistId}>
        {backlineTypes.map((t) => <option key={t} value={t} />)}
      </datalist>
    </>
  );
}
